import React, { useEffect, useMemo, useState } from "react";
import { Practice, PracticeSubject } from "../../models/multiple-question";
import classes from "./practiceHome.module.css";
// @ts-ignore
import mergeClassNames from "merge-class-names";
import { db } from "../../firebase/config";
import { collection, query, where, getDocs } from "firebase/firestore";
import { toast } from "react-toastify";
import LoadingButton from "../../components/LoadingButton";
import useRedirect from "../../hooks/useRedirect";
import LoadingIcon from "../../components/LoadingIcon";
import SubjectIcon from "./SubjectIcon";
import { MdHistory } from "@react-icons/all-files/md/MdHistory";
import { GoTasklist } from "@react-icons/all-files/go/GoTasklist";
import { GoListUnordered } from "@react-icons/all-files/go/GoListUnordered";
import { FaGraduationCap } from "@react-icons/all-files/fa/FaGraduationCap";
import { FaSearch } from "@react-icons/all-files/fa/FaSearch";
import { AiOutlineClose } from "@react-icons/all-files/ai/AiOutlineClose";
import { FiChevronRight } from "@react-icons/all-files/fi/FiChevronRight";
import useHideFooter from "../../hooks/useHideFooter";
import Tags from "../../components/Tags";
import AffiliateSpotlight from "../../components/AffiliateSpotlight";

const SUBJECT_PALETTE = ["#306da6", "#57b056", "#f59e0b", "#8b5cf6", "#ec4899"];

const CHIPS_LIMIT = 8;
const TAGS_SUBJECT_LIMIT = 12;

const ACCENT_PALETTE = [
  "linear-gradient(180deg, #bb2019 0%, #dc2626 100%)",
  "linear-gradient(180deg, #306da6 0%, #3b82f6 100%)",
  "linear-gradient(180deg, #57b056 0%, #22c55e 100%)",
  "linear-gradient(180deg, #f59e0b 0%, #f97316 100%)",
  "linear-gradient(180deg, #8b5cf6 0%, #a855f7 100%)",
];

interface PracticeCardProps {
  practice: Practice;
  accentColor: string;
  subjectName: string;
  onClick: () => void;
}

function PracticeCard({ practice, accentColor, subjectName, onClick }: PracticeCardProps) {
  return (
    <div className={classes.card} onClick={onClick}>
      <div className={classes.cardAccent} style={{ background: accentColor }} />
      <div className={classes.cardBody}>
        <div className={classes.cardHeader}>
          {subjectName && (
            <span className={classes.cardSubjectBadge}>{subjectName}</span>
          )}
          <FiChevronRight className={classes.cardArrow} />
        </div>
        <h3 className={classes.cardTitle}>{practice.title}</h3>
        {practice.description && (
          <p className={classes.cardDescription}>{practice.description}</p>
        )}
        <div className={classes.cardStats}>
          <span className={mergeClassNames(classes.cardStat, classes.cardStatQuestion)}>
            <GoTasklist />
            {practice.numberOfQuestion || 0} câu
          </span>
          {practice.chapters && practice.chapters.length > 0 && (
            <span className={mergeClassNames(classes.cardStat, classes.cardStatChapter)}>
              <GoListUnordered />
              {practice.chapters.length} chương
            </span>
          )}
          <span className={mergeClassNames(classes.cardStat, classes.cardStatHistory)}>
            <MdHistory />
            {practice.numberOfPracticed || 0} đã làm
          </span>
        </div>
      </div>
    </div>
  );
}

function PracticeHome() {
  const [subjectFetching, setSubjectFetching] = useState(false);
  const [subjectError, setSubjectError] = useState(false);
  const [subjects, setSubjects] = useState<PracticeSubject[]>([]);
  const [selectedSubject, setSelectedSubject] = useState<PracticeSubject | null>(null);
  const [practiceFetching, setPracticeFetching] = useState(false);
  const [practiceError, setPracticeError] = useState(false);
  const [practices, setPractices] = useState<Practice[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [chipsExpanded, setChipsExpanded] = useState(false);
  const redirect = useRedirect();
  useHideFooter();

  useEffect(() => {
    document.title = "Grade PTIT | Ôn thi trắc nghiệm";
  }, []);

  useEffect(() => {
    handleSubjectRefresh();
  }, []);

  const handleSubjectRefresh = () => {
    setSubjectFetching(true);
    getDocs(collection(db, "practice-subjects"))
      .then((snapshot) => {
        return snapshot.docs.map((d) => ({
          ...d.data(),
          id: d.id,
        }));
      })
      .then((res) => {
        setSubjects(res as PracticeSubject[]);
        setSelectedSubject(null);
        setPractices([]);
        setSubjectError(false);
      })
      .catch((e) => {
        console.log(e);
        setSubjectError(true);
        toast.error("Không thể tải danh sách môn học");
      })
      .finally(() => {
        setSubjectFetching(false);
      });
  };

  useEffect(() => {
    handlePracticeRefresh();
  }, [selectedSubject]);

  const handlePracticeRefresh = () => {
    if (!selectedSubject || practiceFetching) return;
    setPracticeFetching(true);
    const q = query(
      collection(db, "practices"),
      where("practiceSubjectId", "==", selectedSubject.id)
    );
    getDocs(q)
      .then((snapshot) => {
        return snapshot.docs.map((d) => ({
          ...d.data(),
          id: d.id,
        }));
      })
      .then((res) => {
        setPractices(res as Practice[]);
        setPracticeError(false);
      })
      .catch((e) => {
        console.log(e);
        setPracticeError(true);
        toast.error("Không thể tải danh sách bài luyện tập");
      })
      .finally(() => {
        setPracticeFetching(false);
      });
  };

  const handleSelectPractice = (selectedItem: Practice) => {
    redirect("/practice-review/?id=" + selectedItem.id);
  };

  const filteredSubjects = useMemo(() => {
    if (!searchQuery.trim()) return subjects;
    const q = searchQuery.toLowerCase().trim();
    return subjects.filter(
      (s) =>
        s.name.toLowerCase().includes(q) ||
        (s.keywords || []).some((k) => k.toLowerCase().includes(q))
    );
  }, [subjects, searchQuery]);

  const visibleSubjects = useMemo(() => {
    const isSearching = searchQuery.trim().length > 0;
    if (isSearching || chipsExpanded) return filteredSubjects;
    const sliced = filteredSubjects.slice(0, CHIPS_LIMIT);
    // Always keep the selected subject visible even when collapsed
    if (
      selectedSubject &&
      !sliced.some((s) => s.id === selectedSubject.id) &&
      filteredSubjects.some((s) => s.id === selectedSubject.id)
    ) {
      return [...sliced, filteredSubjects.find((s) => s.id === selectedSubject.id)!];
    }
    return sliced;
  }, [filteredSubjects, chipsExpanded, searchQuery, selectedSubject]);

  const hasMoreChips =
    !searchQuery.trim() && filteredSubjects.length > CHIPS_LIMIT;

  const tags = useMemo(() => {
    return [
      "Ôn thi PTIT",
      "Ôn thi trắc nghiệm",
      "PTIT Quizz",
      "Trắc nghiệm PTIT",
      "Hệ thống ôn thi trắc nghiệm",
      "Ngân hàng câu hỏi",
      ...subjects.slice(0, TAGS_SUBJECT_LIMIT).map((subject) => subject.name),
    ];
  }, [subjects]);

  return (
    <div className={classes.root}>
      {/* ZONE 1 — Hero Banner */}
      <div className={classes.heroBanner}>
        <div className={classes.heroBannerBg} />
        <div className={classes.heroBannerBg2} />
        <div className={classes.heroIconWrapper}>
          <FaGraduationCap className={classes.heroIcon} />
        </div>
        <h1 className={classes.heroTitle}>Ôn thi trắc nghiệm PTIT</h1>
        <p className={classes.heroSubtitle}>Luyện tập mỗi ngày – tự tin thi cử</p>
        {subjects.length > 0 && (
          <div className={classes.heroStats}>
            <span className={classes.heroStatBadge}>{subjects.length} môn học</span>
          </div>
        )}
      </div>

      <div className={classes.pageWrapper}>
        {/* ZONE 2 — Search */}
        <div className={classes.searchSection}>
          <div className={classes.searchWrapper}>
            <FaSearch className={classes.searchIcon} />
            <input
              className={classes.searchInput}
              type="text"
              placeholder="Tìm kiếm môn học..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button
                className={classes.searchClear}
                onClick={() => setSearchQuery("")}
                aria-label="Xóa tìm kiếm"
              >
                <AiOutlineClose />
              </button>
            )}
          </div>
        </div>

        {/* ZONE 3 — Subject Chips */}
        <div className={classes.chipsSection}>
          <div className={classes.sectionHeader}>
            <h2 className={classes.sectionHeading}>Môn học</h2>
          </div>
          {subjectFetching ? (
            <div className={classes.emptyWrapper}>
              <LoadingIcon />
            </div>
          ) : subjectError ? (
            <div className={classes.emptyWrapper}>
              <LoadingButton onClick={handleSubjectRefresh} fetching={subjectFetching}>
                Tải lại
              </LoadingButton>
            </div>
          ) : filteredSubjects.length === 0 ? (
            <div className={classes.emptyWrapper}>
              <FaGraduationCap className={classes.emptyIcon} />
              <span className={classes.emptyMessage}>
                {searchQuery
                  ? `Không tìm thấy môn học "${searchQuery}"`
                  : "Danh sách môn học trống"}
              </span>
            </div>
          ) : (
            <>
              <div className={classes.chipsContainer}>
                {visibleSubjects.map((subject, index) => (
                  <div
                    key={subject.id}
                    className={mergeClassNames(
                      classes.chip,
                      selectedSubject?.id === subject.id ? classes.chipSelected : ""
                    )}
                    style={
                      {
                        "--chip-accent": SUBJECT_PALETTE[index % SUBJECT_PALETTE.length],
                      } as React.CSSProperties
                    }
                    onClick={() => setSelectedSubject(subject)}
                  >
                    <SubjectIcon
                      icon={subject.icon}
                      selected={selectedSubject?.id === subject.id}
                    />
                    <span className={classes.chipName}>{subject.name}</span>
                  </div>
                ))}
              </div>
              {hasMoreChips && (
                <button
                  className={classes.chipsToggle}
                  onClick={() => setChipsExpanded((prev) => !prev)}
                >
                  {chipsExpanded
                    ? "Thu gọn"
                    : `Xem thêm ${filteredSubjects.length - CHIPS_LIMIT} môn`}
                </button>
              )}
            </>
          )}
        </div>

        {/* ZONE 4 — Practice Cards */}
        <div className={classes.practicesSection}>
          <div className={classes.sectionHeader}>
            <h2 className={classes.sectionHeading}>
              {selectedSubject ? selectedSubject.name : "Bài luyện tập"}
            </h2>
          </div>
          {practiceFetching ? (
            <div className={classes.emptyWrapper}>
              <LoadingIcon />
            </div>
          ) : practiceError ? (
            <div className={classes.emptyWrapper}>
              <LoadingButton onClick={handlePracticeRefresh} fetching={practiceFetching}>
                Tải lại
              </LoadingButton>
            </div>
          ) : !selectedSubject ? (
            <div className={classes.emptyWrapper}>
              <FaGraduationCap className={classes.emptyIcon} />
              <span className={classes.emptyMessage}>
                Hãy chọn một môn học để xem bài luyện tập
              </span>
            </div>
          ) : practices.length === 0 ? (
            <div className={classes.emptyWrapper}>
              <FaGraduationCap className={classes.emptyIcon} />
              <span className={classes.emptyMessage}>
                Chưa có bài luyện tập cho môn này
              </span>
            </div>
          ) : (
            <div className={classes.practiceList}>
              {practices.map((practice, index) => (
                <PracticeCard
                  key={practice.id}
                  practice={practice}
                  accentColor={ACCENT_PALETTE[index % ACCENT_PALETTE.length]}
                  subjectName={selectedSubject?.name ?? ""}
                  onClick={() => handleSelectPractice(practice)}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      <AffiliateSpotlight placement="practice-home" />

      <Tags tags={tags} className={classes.tagContainer} />
    </div>
  );
}

export default PracticeHome;
